/**
 * LO QUE SE RELLENA AL CONFIRMAR UN MUESTREO, además de la plaga: la gravedad
 * (obligatoria), a qué FOCO pertenece y cómo está respecto a la vez anterior.
 *
 * Es el mismo bloque en las tres pantallas donde se confirma —panel de triaje,
 * resumen del asistente de captura y vista previa de voz—. La plaga la elige
 * cada pantalla con su propio desplegable; todo lo demás está aquí para que no
 * haya tres versiones de la misma pregunta.
 *
 * EL CAMPO «FOCO» TIENE CINCO CARAS (decisión del 21-09-2026):
 *  · relleno ......... misma línea y misma plaga: llega elegido, con «Cambiar»;
 *  · reabrir ......... el foco se resolvió hace poco: llega elegido como rebrote;
 *  · debes elegir .... hay focos cerca y no está claro: nada elegido, no se
 *                      puede confirmar sin elegir;
 *  · otra plaga ...... en esa línea hay un foco vivo de OTRA plaga: se avisa y
 *                      se elige entre «son distintas» y «es el mismo»;
 *  · nuevo ........... no hay nada cerca.
 *
 * Aquí no se decide nada: las reglas están en `focusLinking.ts` y
 * `confirmForm.ts`. Los `data-*` son para las pruebas de navegador.
 */
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2, RotateCcw, Sparkles } from "lucide-react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/dateFormatters";
import type { ConfirmSamplingForm } from "@/hooks/useConfirmSamplingForm";
import type { ConfirmSeverity, ConfirmVerdict } from "@/hooks/useConfirmScoutingSession";
import { sameChoice, type FocusOption } from "@/lib/pests/confirmForm";
import type { LinkReason } from "@/lib/health/focusLinking";

const SEVERITIES: ConfirmSeverity[] = ["low", "medium", "high", "critical"];
const SEVERITY_INK: Record<ConfirmSeverity, string> = { low: "#2B7DE0", medium: "#E0962E", high: "#E5574E", critical: "#B42318" };
const VERDICTS: { id: ConfirmVerdict; glyph: string }[] = [
  { id: "better", glyph: "▼" },
  { id: "same", glyph: "▬" },
  { id: "worse", glyph: "▲" },
];
/** Los motivos que explican una DUDA. Los que solo dicen «es la misma línea» no se enseñan. */
const SHOWN_REASONS: LinkReason[] = [
  "neighbour_line",
  "far_along_line",
  "sampling_without_row",
  "focus_without_row",
  "row_inactive",
  "multiple_clear",
  "resolved_recently",
  "hint_from_task",
];

/** Un día `yyyy-MM-dd` como fecha LOCAL (con `new Date(iso)` sería medianoche UTC). */
const dayLabel = (iso: string) => formatDate(new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))), "d MMM");

const CHIP = "rounded-xl border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2B7DE0]";

/** Lo que dijo el trabajador al muestrear (D19): se enseña, no se contesta por él. */
export interface WorkerHint {
  answer: string | null;
  verdict: string | null;
  skipReason: string | null;
  /** Se le preguntó (hay pista) aunque no contestara nada útil. */
  asked: boolean;
  /** La pista es sobre un parte aún sin confirmar de la misma línea, no sobre un sitio confirmado. */
  pending?: boolean;
  /** El sitio sobre el que contestó: para decir «lo mismo que el foco n.º 93 de OIDIOPSIS», no solo «lo mismo». */
  focus?: { focusNumber: number; pestName: string; lineNumber: number | null } | null;
}

export function ConfirmSamplingFields({
  form,
  className,
  showSeverity = true,
  samplerIsMe = true,
  worker = null,
}: {
  /** Quien confirma es quien muestreó: entonces «cómo está» lo dice él aquí. */
  samplerIsMe?: boolean;
  worker?: WorkerHint | null;
  form: ConfirmSamplingForm;
  className?: string;
  /** `false` donde la pantalla ya tiene su selector de gravedad (el asistente de captura). */
  showSeverity?: boolean;
}) {
  const { t } = useTranslation();
  if (!form.enabled) return null;

  const focusName = (focusId: string) => t("healthMap.focus.number", { n: form.focusNumber.get(focusId) ?? "—" });

  const optionLabel = (o: FocusOption): string => {
    if (!o.candidate) return form.fieldState === "other_pest" ? t("pests.scouting.focusLink.otherPestDistinct") : t("pests.scouting.focusLink.newFocus");
    const f = o.candidate.focus;
    if (o.candidate.tier === "other_pest") return t("pests.scouting.focusLink.otherPestSame", { pest: f.pestName ?? "—" });
    if (o.candidate.tier === "rebound" && o.candidate.daysSinceResolved != null) {
      return t("pests.scouting.focusLink.reopen", { focus: focusName(f.id), days: o.candidate.daysSinceResolved });
    }
    return focusName(f.id);
  };

  const optionDetail = (o: FocusOption): string | null => {
    if (!o.candidate) return null;
    const f = o.candidate.focus;
    const place =
      f.lineNumber != null
        ? t("pests.scouting.focusLink.focusLabel", { pest: f.pestName ?? "—", line: f.lineNumber, n: f.sampleCount })
        : t("pests.scouting.focusLink.focusLabelNoLine", { pest: f.pestName ?? "—", n: f.sampleCount });
    const why = o.candidate.reasons.filter((r) => SHOWN_REASONS.includes(r)).map((r) => t(`pests.scouting.focusLink.reasons.${r}`));
    return [place, dayLabel(f.lastDate), ...why].join(" · ");
  };

  const prefilled = form.fieldState === "prefilled" || form.fieldState === "reopen";
  const showList = !prefilled || form.expanded;
  const current = form.options.find((o) => sameChoice(o.choice, form.choice)) ?? null;
  const workerAnswer = form.hint?.answer ?? null;

  return (
    <div className={cn("space-y-4", className)} data-testid="confirm-sampling-fields">
      {/* ── Lo que dijo quien muestreó (D19): quien confirma lo VE, no lo contesta por él. ── */}
      {worker && (worker.answer || worker.verdict || worker.skipReason || worker.asked) && (
        <p className="rounded-xl bg-sky-50 px-3 py-2 text-[0.85rem] text-sky-900" data-testid="confirm-worker-hint" data-answer={worker.answer ?? ""} data-verdict={worker.verdict ?? ""} data-skip={worker.skipReason ?? ""}>
          {worker.skipReason
            ? t("pests.scouting.focusLink.workerNotAsked", { reason: t(`pests.scouting.focusLink.skip.${worker.skipReason}`) })
            : worker.answer
              ? [
                  worker.focus
                    ? t(
                        worker.answer === "same" ? "pests.scouting.focusLink.workerSameAs" : worker.answer === "different" ? "pests.scouting.focusLink.workerDifferentFrom" : "pests.scouting.focusLink.workerUnknownAbout",
                        { n: worker.focus.focusNumber, pest: worker.focus.pestName.toLocaleUpperCase(), line: worker.focus.lineNumber ?? "?" },
                      )
                    : t("pests.scouting.focusLink.workerSays", { answer: t(`pests.scouting.focusLink.answer.${worker.answer}`) }) +
                      (worker.pending && worker.answer === "same" ? ` ${t("pests.scouting.focusLink.samePending")}` : ""),
                  worker.verdict ? t(`worker.incident.followup.${worker.verdict}`) : worker.answer === "same" ? t("pests.scouting.focusLink.workerNoVerdict") : null,
                ].filter(Boolean).join(" · ")
              : t("pests.scouting.focusLink.workerNoAnswer")}
        </p>
      )}
      {!worker && workerAnswer && (
        <p className="rounded-xl bg-sky-50 px-3 py-2 text-[0.85rem] text-sky-900" data-testid="confirm-worker-hint" data-answer={workerAnswer}>
          {t("pests.scouting.focusLink.workerSays", { answer: t(`pests.scouting.focusLink.answer.${workerAnswer}`) })}
        </p>
      )}

      {!showSeverity && form.blocked === "severity" && (
        <p className="text-[0.85rem] font-semibold text-amber-800" data-testid="confirm-severity-missing">
          {t("pests.scouting.focusLink.errors.severity")}
        </p>
      )}

      {/* ── Gravedad: obligatoria. La de la IA llega elegida, y se dice que es suya. ── */}
      {showSeverity && <div className="space-y-1.5">
        <Label>{t("pests.scouting.focusLink.severityRequired")}</Label>
        <div className="grid grid-cols-4 gap-1.5" role="radiogroup" aria-label={t("pests.scouting.focusLink.severityRequired")}>
          {SEVERITIES.map((s) => {
            const on = form.severity === s;
            return (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => form.setSeverity(s)}
                data-testid={`confirm-severity-${s}`}
                data-selected={on}
                className={cn(CHIP, "px-1 text-center text-[0.82rem] font-semibold", on ? "text-white" : "bg-white text-slate-700")}
                style={on ? { background: SEVERITY_INK[s], borderColor: SEVERITY_INK[s] } : undefined}
              >
                {t(`worker.incident.severityLevels.${s}`)}
              </button>
            );
          })}
        </div>
        {form.severityIsAi && (
          <p className="flex items-center gap-1 text-[0.75rem] text-slate-500" data-testid="confirm-severity-ai">
            <Sparkles className="h-3 w-3" aria-hidden="true" />
            {t("pests.scouting.focusLink.aiSuggested")}
          </p>
        )}
      </div>}

      {/* ── Foco. Sin sector no hay foco posible: el campo no sale. ── */}
      {form.hasBlock && (
        <div className="space-y-1.5" data-testid="confirm-focus-field" data-field-state={form.loading ? "loading" : form.loadError ? "error" : form.fieldState ?? "idle"}>
          <Label>{t("pests.scouting.focusLink.field")}</Label>

          {form.loading && (
            <div className="flex items-center gap-2 rounded-xl border bg-white px-3 py-2.5 text-[0.85rem] text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t("common.loading")}
            </div>
          )}

          {!form.loading && form.loadError && (
            <div className="flex items-center justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-[0.85rem] text-red-800">
              <span>{t("pests.scouting.focusLink.loadError")}</span>
              <button type="button" onClick={() => form.refetch()} className="inline-flex shrink-0 items-center gap-1 font-semibold underline" data-testid="confirm-focus-retry">
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                {t("common.retry")}
              </button>
            </div>
          )}

          {!form.loading && !form.loadError && form.result && (
            <>
              {form.fieldState === "must_choose" && (
                <p className="text-[0.85rem] font-semibold text-amber-800" data-testid="confirm-focus-must-choose">
                  {t("pests.scouting.focusLink.mustChoose")}
                  <span className="block font-normal text-amber-700">{t("pests.scouting.focusLink.mustChooseHint")}</span>
                </p>
              )}
              {form.fieldState === "other_pest" && form.result.proposal.kind === "other_pest" && (
                <p className="flex items-start gap-1.5 text-[0.85rem] font-semibold text-amber-800" data-testid="confirm-focus-other-pest">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  {t("pests.scouting.focusLink.otherPestTitle", {
                    pest: form.options.find((o) => o.candidate?.focus.id === (form.result!.proposal as { focusId: string }).focusId)?.candidate?.focus.pestName ?? "—",
                  })}
                </p>
              )}

              {/* Relleno: se enseña lo elegido y un «Cambiar» que abre la lista entera. */}
              {prefilled && !form.expanded && current && (
                <div className="flex items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5" data-testid="confirm-focus-prefilled" data-focus-id={current.candidate?.focus.id ?? "new"}>
                  <div className="min-w-0">
                    <p className="truncate text-[0.9rem] font-semibold text-emerald-900">{optionLabel(current)}</p>
                    <p className="truncate text-[0.78rem] text-emerald-800">
                      {form.fieldState === "prefilled" && sameChoice(current.choice, { kind: "join", focusId: (form.result.proposal as { focusId: string }).focusId, adopt: false })
                        ? t("pests.scouting.focusLink.prefilled")
                        : optionDetail(current)}
                    </p>
                  </div>
                  <button type="button" onClick={() => form.setExpanded(true)} className="shrink-0 text-[0.85rem] font-semibold text-emerald-900 underline" data-testid="confirm-focus-change">
                    {t("pests.scouting.focusLink.change")}
                  </button>
                </div>
              )}

              {showList && (
                <div className="space-y-1.5" role="radiogroup" aria-label={t("pests.scouting.focusLink.field")}>
                  {form.options.map((o) => {
                    const on = sameChoice(o.choice, form.choice);
                    const detail = optionDetail(o);
                    return (
                      <button
                        key={o.key}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => form.setChoice(o.choice)}
                        data-testid="confirm-focus-option"
                        data-option-key={o.key}
                        data-tier={o.candidate?.tier ?? "new"}
                        data-selected={on}
                        className={cn(CHIP, "block w-full", on ? "border-[#2B7DE0] bg-[#EAF3FD]" : "bg-white")}
                      >
                        <span className="block text-[0.9rem] font-semibold text-slate-900">{optionLabel(o)}</span>
                        {detail && <span className="block text-[0.78rem] text-slate-600">{detail}</span>}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* D (23-09-2026): este muestreo se vio ANTES de que se cerrara el foco elegido.
                  Quien confirma decide: reabrirlo con él, o que entre ya cerrado. Sin contestar
                  no se confirma. */}
              {form.closedOn && (
                <div className="space-y-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5" data-testid="confirm-closed-focus">
                  <p className="text-[0.85rem] font-semibold text-amber-900">
                    {t("pests.scouting.focusLink.closedFocus.title", { date: dayLabel(form.closedOn) })}
                  </p>
                  <div className="grid grid-cols-2 gap-1.5" role="radiogroup" aria-label={t("pests.scouting.focusLink.closedFocus.title", { date: dayLabel(form.closedOn) })}>
                    {(["reopen", "keep_closed"] as const).map((c) => {
                      const on = form.closedChoice === c;
                      return (
                        <button
                          key={c}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          onClick={() => form.setClosedChoice(c)}
                          data-testid={`confirm-closed-focus-${c}`}
                          data-selected={on}
                          className={cn(CHIP, "px-2 text-center text-[0.85rem] font-semibold", on ? "border-slate-800 bg-slate-800 text-white" : "bg-white text-slate-800")}
                        >
                          {t(`pests.scouting.focusLink.closedFocus.${c}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {form.result.antecedents.length > 0 && (
                <p className="text-[0.78rem] text-slate-500" data-testid="confirm-focus-antecedent">
                  {t("pests.scouting.focusLink.antecedent", { focus: focusName(form.result.antecedents[0].id) })}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Avance: solo si hay «vez anterior» (al unirse a un foco) Y lo muestreó quien
             confirma. Un parte de un trabajador trae su respuesta (arriba); el admin no
             decide «cómo está» de una planta que no ha visto (D19). ── */}
      {form.hasBlock && form.showVerdict && samplerIsMe && (
        <div className="space-y-1.5" data-testid="confirm-verdict">
          <Label>{t("pests.scouting.focusLink.verdict")}</Label>
          <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label={t("pests.scouting.focusLink.verdict")}>
            {VERDICTS.map((v) => {
              const on = form.verdict === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  // Tocar el elegido lo quita: el avance es opcional.
                  onClick={() => form.setVerdict(on ? null : v.id)}
                  data-testid={`confirm-verdict-${v.id}`}
                  data-selected={on}
                  className={cn(CHIP, "px-1 text-center text-[0.85rem] font-semibold", on ? "border-slate-800 bg-slate-800 text-white" : "bg-white text-slate-700")}
                >
                  <span aria-hidden="true" className="mr-1">{v.glyph}</span>
                  {t(`healthMap.focus.evolution.${v.id}`)}
                </button>
              );
            })}
          </div>
          {form.conflict && (
            <p className="flex items-start gap-1.5 text-[0.78rem] text-amber-800" data-testid="confirm-verdict-conflict" data-conflict={form.conflict}>
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t(`pests.scouting.focusLink.conflict.${form.conflict}`, {
                previous: t(`worker.incident.severityLevels.${form.chosen?.focus.lastSeverity ?? "medium"}`),
              })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
